import {
  Alert,
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import { useState } from "react";
import {
  Distributor,
  DistributorAuditRecord,
  useGetDistributorAuditQuery,
} from "../../api/admin/distributors/distributorsApi";

const outcomes = [
  "succeeded", "failed", "accepted", "partial", "unconfirmed",
];

export function auditTime(value: string) {
  return new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value)) + " UTC+8";
}

type Props = {
  distributors: Distributor[];
  distributorId?: string;
  onDistributorChange: (id: string) => void;
};

export default function AuditHistory({
  distributors,
  distributorId,
  onDistributorChange,
}: Props) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [cursor, setCursor] = useState<string>();
  const [previous, setPrevious] = useState<Array<string | undefined>>([]);
  const { data, isFetching, error, refetch } = useGetDistributorAuditQuery({
    distributorId: distributorId || "",
    ...filters,
    cursor,
  }, { skip: !distributorId });

  const resetPage = () => {
    setCursor(undefined);
    setPrevious([]);
  };

  const filter = (key: string, value?: string) => {
    setFilters((current) => ({ ...current, [key]: value || "" }));
    resetPage();
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Select distributor"
          aria-label="Distributor"
          style={{ minWidth: 260 }}
          value={distributorId}
          options={distributors.map((distributor) => ({
            value: distributor.id,
            label: `${distributor.name} (${distributor.code})`,
          }))}
          onChange={(id) => {
            resetPage();
            onDistributorChange(id);
          }}
        />
        <DatePicker.RangePicker
          onChange={(_, dates) => {
            setFilters((current) => ({
              ...current,
              from: dates[0],
              to: dates[1],
            }));
            resetPage();
          }}
        />
        <Input.Search
          placeholder="Actor username or email"
          aria-label="Actor"
          allowClear
          onSearch={(value) => filter("actor", value)}
        />
        <Input.Search
          placeholder="Action, e.g. staff.disable"
          aria-label="Action"
          allowClear
          onSearch={(value) => filter("action", value)}
        />
        <Select
          placeholder="Outcome"
          aria-label="Outcome"
          allowClear
          style={{ minWidth: 190 }}
          options={outcomes.map((outcome) => ({
            value: outcome,
            label: outcome === "unconfirmed"
              ? "Outcome unconfirmed"
              : outcome,
          }))}
          onChange={(value) => filter("outcome", value)}
        />
        <Button disabled={!distributorId} onClick={() => refetch()}>
          Refresh
        </Button>
      </Space>
      {error && <Alert type="error" message="Unable to load audit history" />}
      <Table<DistributorAuditRecord>
        rowKey="EventId"
        loading={isFetching}
        dataSource={data?.records || []}
        pagination={false}
        locale={{ emptyText: distributorId
          ? "No matching events on this page"
          : "Select a distributor to view its history" }}
        columns={[
          {
            title: "Time (UTC+8)",
            dataIndex: "CreatedAt",
            render: auditTime,
          },
          { title: "Actor", dataIndex: "Actor" },
          { title: "Action", dataIndex: "Action" },
          { title: "Source", dataIndex: "Source" },
          {
            title: "Outcome",
            dataIndex: "Outcome",
            render: (outcome: string) => (
              <Tag>{outcome === "unconfirmed"
                ? "Outcome unconfirmed"
                : outcome}</Tag>
            ),
          },
        ]}
        expandable={{
          expandedRowRender: (record) => (
            <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {JSON.stringify({
                distributor: {
                  id: record.DistributorId,
                  code: record.DistributorCode,
                  name: record.DistributorName,
                },
                actor: record.Actor,
                actorEmail: record.ActorEmail,
                actorSub: record.ActorSub,
                details: record.Details,
                result: record.Result,
                requestId: record.RequestId,
                sessionId: record.SessionId,
              }, null, 2)}
            </pre>
          ),
        }}
      />
      <Space>
        <Button
          disabled={!previous.length || isFetching}
          onClick={() => {
            setCursor(previous[previous.length - 1]);
            setPrevious(previous.slice(0, -1));
          }}
        >
          Previous
        </Button>
        <Button
          disabled={!data?.cursor || isFetching}
          onClick={() => {
            setPrevious([...previous, cursor]);
            setCursor(data?.cursor || undefined);
          }}
        >
          Next
        </Button>
      </Space>
    </Space>
  );
}
